export type DocumentVersionRef = { version: number; document?: { final?: unknown } | null };

/** The highest-numbered version, whatever order the list is in. The server lists newest first. */
export const latestVersion = <V extends { version: number }>(
  versions: readonly V[]
): V | undefined =>
  versions.reduce<V | undefined>(
    (latest, each) => (!latest || each.version > latest.version ? each : latest),
    undefined
  );

/**
 * The version a document opens on when nothing names one: its highest-numbered Final version, else
 * its latest. Final reads null while designs are off, so those agencies always open the latest.
 */
export const defaultDocumentVersion = <V extends DocumentVersionRef>(
  versions: readonly V[]
): V | undefined =>
  latestVersion(versions.filter((each) => each.document?.final)) ?? latestVersion(versions);

/**
 * The version a card naming `named` opens: the document's default version, unless that is older than
 * `named`, so a card for a version Cara wrote past a Final opens the version it names or a later one.
 */
export const openedDocumentVersion = <V extends DocumentVersionRef>(
  versions: readonly V[],
  named: V
): V => {
  const preferred = defaultDocumentVersion(versions);
  return preferred && preferred.version >= named.version
    ? preferred
    : (latestVersion(versions) ?? named);
};
